import {
  RESET_PART_INFO,
  RESET_PARTS,
  type ResetPart,
  resetClosure,
  resetReasons,
} from "@bl/domain";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { History, RotateCcw, Save, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { send, useChange, useSession } from "../api";
import { Dialog, DialogForm, ErrorNote, Field, Toolbar, usePageTitle } from "../components/ui";
import { showDate } from "../format";
import { useLayout } from "../layout/LayoutProvider";
import type { ColorUnit } from "../layout/layout";
import { useNotify } from "../notify";
import { Equipment } from "./Equipment";
import { InventoryManagement } from "./InventoryManagement";
import { MakerNames } from "./MakerNames";
import { OllieImportSection } from "./StockImport";

export function SettingsPage() {
  usePageTitle("Settings");
  const layoutApi = useLayout();
  const { layout, update, saved, activeId, modified } = layoutApi;
  const [newName, setNewName] = useState("");
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const prefs = layout.preferences;
  const setPrefs = (patch: Partial<typeof prefs>) =>
    update((l) => ({ ...l, preferences: { ...l.preferences, ...patch } }));
  const active = saved.find((s) => s.id === activeId);

  return (
    <>
      <Toolbar />
      <div className="page-body centered narrow stack">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="muted" style={{ marginTop: -8 }}>
            Layouts and display are kept in this browser on this device only: your phone and your
            desktop each remember their own. Everything below them is shared by the whole brewery.
          </p>
        </div>

        <section id="layouts" className="section">
          <div className="section-header" style={{ cursor: "default" }}>
            <h2>Layouts on this device</h2>
            <span className="aside">
              {active
                ? `Using "${active.name}"${modified ? ", changed since saved" : ""}`
                : "Unsaved"}
            </span>
          </div>
          <div className="section-body stack">
            <p className="muted" style={{ margin: 0 }}>
              A layout is the view, columns, sort, filters, units and collapsed sections you have
              set up. Save one under a name to come back to it.
            </p>
            {saved.length ? (
              <div className="list-rows">
                {saved.map((s) => (
                  <div key={s.id}>
                    {renaming?.id === s.id ? (
                      <form
                        className="row"
                        style={{ flex: 1 }}
                        onSubmit={(e) => {
                          e.preventDefault();
                          layoutApi.rename(s.id, renaming.name);
                          setRenaming(null);
                        }}
                      >
                        <input
                          className="input"
                          style={{ flex: 1 }}
                          aria-label="Layout name"
                          value={renaming.name}
                          onChange={(e) => setRenaming({ id: s.id, name: e.target.value })}
                          // biome-ignore lint/a11y/noAutofocus: renaming in place
                          autoFocus
                        />
                        <button type="submit" className="button small primary">
                          Save
                        </button>
                      </form>
                    ) : (
                      <>
                        <strong>{s.name}</strong>
                        {s.id === activeId ? (
                          <span className="chip accent plain">In use</span>
                        ) : null}
                        <span className="faint">saved {showDate(s.savedAt)}</span>
                        <span className="spacer" />
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => layoutApi.apply(s.id)}
                        >
                          Use
                        </button>
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => layoutApi.overwrite(s.id)}
                          title="Replace this saved layout with what is on screen now"
                        >
                          Update
                        </button>
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => setRenaming({ id: s.id, name: s.name })}
                        >
                          Rename
                        </button>
                        <button
                          type="button"
                          className="link-button"
                          style={{ color: "var(--danger)" }}
                          onClick={() => layoutApi.remove(s.id)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : null}
            <form
              className="row"
              onSubmit={(e) => {
                e.preventDefault();
                layoutApi.saveAs(newName || `Layout ${saved.length + 1}`);
                setNewName("");
              }}
            >
              <input
                className="input"
                style={{ maxWidth: 280 }}
                placeholder="Name, e.g. Brewhouse iPad"
                aria-label="Layout name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <button type="submit" className="button primary">
                <Save size={16} /> Save current layout
              </button>
              <button type="button" className="button" onClick={layoutApi.reset}>
                <RotateCcw size={16} /> Reset to default
              </button>
            </form>
          </div>
        </section>

        <section id="display" className="section">
          <div className="section-header" style={{ cursor: "default" }}>
            <h2>Display</h2>
          </div>
          <div className="section-body form-grid">
            <Field label="Theme">
              <select
                className="select"
                value={prefs.theme}
                onChange={(e) => setPrefs({ theme: e.target.value as typeof prefs.theme })}
              >
                <option value="auto">Match this device</option>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </Field>
            <Field label="Malt color in" hint="Converted values are marked with ≈">
              <select
                className="select"
                value={prefs.colorUnit}
                onChange={(e) => setPrefs({ colorUnit: e.target.value as ColorUnit })}
              >
                <option value="lovibond">°Lovibond</option>
                <option value="srm">SRM</option>
                <option value="ebc">EBC</option>
              </select>
            </Field>
            <Field label="Amounts in">
              <select
                className="select"
                value={layout.inventory.quantityMode}
                onChange={(e) =>
                  update((l) => ({
                    ...l,
                    inventory: { ...l.inventory, quantityMode: e.target.value as "unit" | "pack" },
                  }))
                }
              >
                <option value="unit">Units (lb, g, gal)</option>
                <option value="pack">Packs (bags, boxes) where known</option>
              </select>
            </Field>
          </div>
        </section>

        <section className="section">
          <div className="section-header" style={{ cursor: "default" }}>
            <h2>History</h2>
          </div>
          <div className="section-body stack">
            <p className="muted" style={{ margin: 0 }}>
              Every change to stock, newest first: what came in, what was used, counted, moved and
              lent, and what was undone.
            </p>
            <div className="row">
              <Link to="/movements" className="button">
                <History size={16} /> Open history
              </Link>
            </div>
          </div>
        </section>

        <Equipment />

        <MakerNames />

        <InventoryManagement />

        <OllieImportSection />

        <ResetTenant />

        <section id="api" className="section">
          <div className="section-header" style={{ cursor: "default" }}>
            <h2>API</h2>
          </div>
          <div className="section-body">
            <p className="muted" style={{ marginTop: 0 }}>
              This app uses the same API anyone can build on. Its description is at{" "}
              <a href="/api/v1/openapi.json">/api/v1/openapi.json</a>.
            </p>
          </div>
        </section>
      </div>
    </>
  );
}
/**
 * Emptying the brewery while the product is being built, so the same delivery can go through the
 * review screen again without an SSH session (Tyler, 2026-09-21), a part at a time, as a browser clears
 * its data (Tyler, 2026-10-07: Scott rebuilt the equipment after every reset). A part another depends
 * on is ticked with it and cannot be unticked, and says why (`RESET_PARTS` in `@bl/domain`).
 *
 * Shown only where the server says it will do it, which is a deployment that set ALLOW_TENANT_RESET.
 * A server holding a brewery's real stock does not, and this section is then not there at all.
 */
function ResetTenant() {
  const { data: session } = useSession();
  const [choosing, setChoosing] = useState(false);
  const [chosen, setChosen] = useState<ReadonlySet<ResetPart>>(new Set());
  const queryClient = useQueryClient();
  const notify = useNotify();
  const going = resetClosure([...chosen]);
  const reasons = resetReasons([...chosen]);
  const everything = going.length === RESET_PARTS.length;
  const reset = useChange(async () => send.resetTenant([...chosen]), {
    onSuccess: async (done) => {
      await queryClient.invalidateQueries();
      setChoosing(false);
      setChosen(new Set());
      notify(
        done.cleared.length === RESET_PARTS.length
          ? "The brewery is empty"
          : `Emptied: ${done.cleared.map((p) => RESET_PART_INFO[p].label.toLowerCase()).join(", ")}`,
      );
    },
  });
  const toggle = (part: ResetPart) =>
    setChosen((was) => {
      const next = new Set(was);
      if (!next.delete(part)) next.add(part);
      return next;
    });
  // What a part leaves behind, said only where it is true of what is going.
  const leaves = (part: ResetPart) =>
    (part === "inventory" && going.includes("recipes")) ||
    (part === "invoices" && going.includes("inventory"))
      ? undefined
      : RESET_PART_INFO[part].leaves;

  if (!session?.canResetTenant) return null;

  return (
    <section id="empty-this-brewery" className="section danger-zone">
      <div className="section-header" style={{ cursor: "default" }}>
        <h2>Empty this brewery</h2>
        <span className="aside">While we are building</span>
      </div>
      <div className="section-body stack">
        <p className="muted" style={{ marginTop: 0 }}>
          Throws away what you choose: inventory, invoices, recipes, brews, the calendar, equipment,
          locations, or all of it, and puts back what a new brewery starts with where it went. Keep
          your tanks and brew systems while you clear everything else. The shared catalog is
          untouched.
        </p>
        <div className="row">
          <button type="button" className="button danger" onClick={() => setChoosing(true)}>
            <TriangleAlert size={16} /> Empty this brewery
          </button>
        </div>
        <ErrorNote error={reset.error} />
      </div>
      <Dialog open={choosing} onClose={() => setChoosing(false)} title="Empty this brewery">
        <DialogForm
          onSubmit={() => going.length && reset.mutate(undefined)}
          footer={
            <>
              <button type="button" className="button" onClick={() => setChoosing(false)}>
                Keep it
              </button>
              <button
                type="submit"
                className="button danger"
                disabled={going.length === 0 || reset.isPending}
              >
                {reset.isPending
                  ? "Emptying"
                  : everything
                    ? "Empty everything"
                    : going.length === 0
                      ? "Empty"
                      : `Empty ${going.length} ${going.length === 1 ? "part" : "parts"}`}
              </button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            Choose what to throw away. There is no undo, and nothing is kept anywhere to put it back
            from.
          </p>
          <label className="check-row reset-all">
            <input
              type="checkbox"
              checked={everything}
              onChange={() => setChosen(new Set(everything ? [] : RESET_PARTS))}
            />
            <strong>Everything</strong>
          </label>
          <ul className="reset-parts">
            {RESET_PARTS.map((part) => {
              const info = RESET_PART_INFO[part];
              const forced = reasons.get(part);
              const on = going.includes(part);
              return (
                <li key={part}>
                  <label className="check-row">
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={forced !== undefined}
                      onChange={() => toggle(part)}
                    />
                    <span>
                      <strong>{info.label}</strong>
                      <span className="faint"> · {info.holds}</span>
                      {forced ? <span className="reset-note">{forced}</span> : null}
                      {on && !forced && leaves(part) ? (
                        <span className="reset-note muted">{leaves(part)}</span>
                      ) : null}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
          <ErrorNote error={reset.error} />
        </DialogForm>
      </Dialog>
    </section>
  );
}
